"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const CODE_A_LOWER = 97;
const CODE_A_UPPER = 65;

/**
 * Eleven script letters were encoded in the Letterlike Symbols block long
 * before the Mathematical Alphanumeric Symbols script alphabet existed, so
 * the script block (U+1D49C–U+1D4CF) reserves their slots. They must be
 * substituted explicitly.
 */
const SCRIPT_EXCEPTIONS = new Map<string, string>([
  ["B", "ℬ"],
  ["E", "ℰ"],
  ["F", "ℱ"],
  ["H", "ℋ"],
  ["I", "ℐ"],
  ["L", "ℒ"],
  ["M", "ℳ"],
  ["R", "ℛ"],
  ["e", "ℯ"],
  ["g", "ℊ"],
  ["o", "ℴ"],
]);

function buildScriptMap(
  lowerStart: number,
  upperStart: number,
  exceptions?: Map<string, string>
): Map<string, string> {
  const map = new Map<string, string>();
  for (let i = 0; i < 26; i++) {
    map.set(
      String.fromCharCode(CODE_A_LOWER + i),
      String.fromCodePoint(lowerStart + i)
    );
    map.set(
      String.fromCharCode(CODE_A_UPPER + i),
      String.fromCodePoint(upperStart + i)
    );
  }
  if (exceptions) {
    for (const [char, replacement] of exceptions) {
      map.set(char, replacement);
    }
  }
  return map;
}

// Bold script: 𝓪 / 𝓐 — a contiguous block with no reserved slots.
const BOLD_SCRIPT_MAP = buildScriptMap(0x1_d4_ea, 0x1_d4_d0);
// Light script: 𝒶 / 𝒜 — has reserved slots filled from Letterlike Symbols.
const LIGHT_SCRIPT_MAP = buildScriptMap(
  0x1_d4_b6,
  0x1_d4_9c,
  SCRIPT_EXCEPTIONS
);

function transform(text: string, map: Map<string, string>): string {
  return [...text].map((ch) => map.get(ch) ?? ch).join("");
}

function ResultCard({
  label,
  value,
  note,
  strings,
}: {
  label: string;
  value: string;
  note?: string;
  strings: typeof CURSIVE_TEXT_GENERATOR_STRINGS;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
          disabled={value.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
        {value.length > 0 ? (
          value
        ) : (
          <span className="text-muted-foreground">
            {strings.typeAboveToSeeOutput}
          </span>
        )}
      </p>
      {note && <p className="mt-2 text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CURSIVE_TEXT_GENERATOR_STRINGS = {
  inputText: "Input Text",
  inputPlaceholder: "Type or paste your text here...",
  results: "Results",
  boldScriptLabel: "Bold script (𝓪𝓫𝓬)",
  lightScriptLabel: "Light script (𝒶𝒷𝒸)",
  lightScriptNote:
    "Eleven letters (ℬ ℰ ℱ ℋ ℐ ℒ ℳ ℛ ℯ ℊ ℴ) come from an older Unicode block and may render slightly differently in some fonts.",
  copied: "Copied!",
  copy: "Copy",
  typeAboveToSeeOutput: "Type above to see output…",
} as const;

export default function CursiveTextGeneratorWidget() {
  const [input, setInput] = useState("");
  const strings = useToolUi(CURSIVE_TEXT_GENERATOR_STRINGS);

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="cursive-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="cursive-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Results */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.results}
        </h2>
        <div className="space-y-3">
          <ResultCard
            label={strings.boldScriptLabel}
            strings={strings}
            value={transform(input, BOLD_SCRIPT_MAP)}
          />
          <ResultCard
            label={strings.lightScriptLabel}
            note={strings.lightScriptNote}
            strings={strings}
            value={transform(input, LIGHT_SCRIPT_MAP)}
          />
        </div>
      </section>
    </>
  );
}
